import { useEffect, useMemo, useRef, useState } from "react";
import { Hash, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setAllSerialsChecked,
  setGroupSerialsChecked,
  setSerialChecked,
} from "@/store/serialFilterSlice";
import { useTranslation } from "@/shared/i18n";
import { serialList } from "@/widgets/common/serialList/state";
import { useSerialList } from "@/widgets/common/serialList/useSerialList";
import { groupSerialsByMachine, filterSerialsByQuery } from "./logic";

/**
 * App-wide serial filter (FR serial filter): a toolbar button opening the
 * modal with the shared serial list, grouped by machine line (HD / HDX /
 * NX, …). Check/uncheck is instant — the Redux `serialFilter` slice stores
 * the CHECKED mpKeys (default: all checked) and is what widgets will later
 * filter their data by. The list comes from the app-wide `useSerialList`
 * cache (one fetch per session).
 *
 * UX contract (ui-ux-pro-max review):
 * - whole row is the hit target (≥44px, label-for) — no pin-point checkbox taps;
 * - loading = skeleton rows; failure = alert + visible Retry (recovery path);
 * - one primary action ("Done") closes the modal; Check all stays secondary.
 */
export function SerialFilterDialog({
  variant = "sidebar",
}: {
  /** `sidebar`: full entry in the desktop rail; `icon`: icon-only mobile bar. */
  variant?: "sidebar" | "icon";
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const { serials, loading, error } = useSerialList();
  const selected = useAppSelector((state) => state.serialFilter.selected);
  // One Set for the rows — selectors never run inside the row `.map()`
  // (hooks must be unconditional at the component top level).
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const visible = useMemo(
    () => filterSerialsByQuery(serials, query),
    [serials, query],
  );
  // Grouped by machine line (first-seen order preserved) for the modal list.
  const groups = useMemo(() => groupSerialsByMachine(visible), [visible]);
  const checkedCount = serials.filter((entry) => selectedSet.has(entry.mpKey)).length;
  const allChecked = checkedCount === serials.length && serials.length > 0;
  const searching = query.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {variant === "icon" ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label={t("serials.filterTitle")}
            title={t("serials.filterTitle")}
            className="ml-auto text-zinc-300 hover:bg-zinc-800 hover:text-white"
          >
            <Hash className="size-4" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 px-3 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white"
          >
            <Hash className="size-4 shrink-0" />
            <span className="truncate">{t("serials.title")}</span>
            <span className="ml-auto text-xs text-zinc-500">
              {checkedCount}/{serials.length}
            </span>
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="flex flex-col gap-4 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("serials.filterTitle")}</DialogTitle>
          <DialogDescription>{t("serials.filterDescription")}</DialogDescription>
        </DialogHeader>

        {/* Search: labeled input (sr-only label) + icon affordance + a clear
            button once the user typed — no dead-end placeholder-only field. */}
        <div className="relative">
          <label htmlFor="serial-filter-search" className="sr-only">
            {t("serials.searchLabel")}
          </label>
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            id="serial-filter-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("serials.searchPlaceholder")}
            className="h-10 w-full rounded-md border border-input bg-background pr-9 pl-9 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          />
          {searching && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label={t("toolbar.clear")}
              className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>

        <div className="gtl-scroll max-h-[50dvh] min-h-0 overflow-y-auto rounded-md border">
          {loading && serials.length === 0 && <SkeletonRows />}

          {!loading && error && serials.length === 0 && (
            <div role="alert" className="flex flex-col items-start gap-3 p-4">
              <p className="text-sm text-destructive">{t("widget.failedToLoad")}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void dispatch(serialList.fetch(undefined))}
              >
                {t("toolbar.refresh")}
              </Button>
            </div>
          )}

          {/* Search miss: say so against the typed query (recoverable by the
              clear button above) — never a generic "no data". */}
          {!loading && !error && visible.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">
              {searching
                ? t("serials.noMatch", { query: query.trim() })
                : t("widget.noData")}
            </p>
          )}

          {groups.map(({ group, serials: groupSerials }) => {
            const groupChecked = groupSerials.filter((entry) =>
              selectedSet.has(entry.mpKey),
            ).length;
            const groupAll = groupChecked === groupSerials.length;
            const groupSome = groupChecked > 0 && !groupAll;
            return (
              <section key={group}>
                {/* Group row: one checkbox toggles the whole machine line
                    (tri-state — dash when only part of the group is on). */}
                <div className="sticky top-0 flex items-center gap-2 border-b bg-background px-3 py-2">
                  <GroupCheckbox
                    all={groupAll}
                    some={groupSome}
                    disabled={loading}
                    ariaLabel={t("serials.groupToggle", { group })}
                    onToggle={(checked) =>
                      dispatch(
                        setGroupSerialsChecked({
                          mpKeys: groupSerials.map((entry) => entry.mpKey),
                          checked,
                        }),
                      )
                    }
                  />
                  <h4 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                    {group}
                  </h4>
                  <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                    {groupChecked}/{groupSerials.length}
                  </span>
                </div>
                <ul className="divide-y">
                  {groupSerials.map((entry) => {
                    const checked = selectedSet.has(entry.mpKey);
                    return (
                      // The FULL row is the hit target (≥44px) — no
                      // pin-point checkbox tapping; hover shows the row.
                      // Indented one level under the group checkbox so the
                      // child/parent nesting reads at a glance.
                      <li key={entry.mpKey} className="border-b last:border-b-0">
                        <label className="flex min-h-11 cursor-pointer items-center gap-3 py-2 pr-3 pl-9 text-sm transition-colors hover:bg-accent/60 focus-within:bg-accent/60">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(event) =>
                              dispatch(
                                setSerialChecked({
                                  mpKey: entry.mpKey,
                                  checked: event.target.checked,
                                }),
                              )
                            }
                            className="size-4 shrink-0 accent-emerald-500"
                          />
                          <span className="truncate">{entry.serial}</span>
                          <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                            {entry.mpKey}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>

        <DialogFooter className="items-center gap-2 sm:justify-between">
          <span className="text-sm tabular-nums text-muted-foreground">
            {t("serials.selectedCount", {
              checked: checkedCount,
              total: serials.length,
            })}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                dispatch(setAllSerialsChecked(serials.map((entry) => entry.mpKey)))
              }
              disabled={allChecked}
            >
              {t("serials.checkAll")}
            </Button>
            {/* Primary action: explicit close-confirm — the footer ends the
                task; the X stays the escape route. */}
            <Button size="sm" asChild>
              <DialogClose>{t("serials.done")}</DialogClose>
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Loading placeholder (skeleton rows keep the list layout stable). */
function SkeletonRows() {
  return (
    <div aria-hidden className="animate-pulse">
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className="flex items-center gap-3 border-b px-3 py-3 last:border-b-0">
          <div className="size-4 shrink-0 rounded-sm bg-muted" />
          <div className="h-3.5 w-40 rounded-sm bg-muted" />
          <div className="ml-auto h-3.5 w-24 rounded-sm bg-muted" />
        </div>
      ))}
    </div>
  );
}

/**
 * Machine-line group checkbox: `checked` only when every serial of the
 * group is on, `indeterminate` (dash) when part of it is. The native
 * `indeterminate` flag is DOM-only — set it through a ref in an effect.
 */
function GroupCheckbox({
  all,
  some,
  disabled,
  ariaLabel,
  onToggle,
}: {
  all: boolean;
  some: boolean;
  disabled: boolean;
  ariaLabel: string;
  onToggle: (checked: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = some;
    }
  }, [some]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={all}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(event) => onToggle(event.target.checked)}
      className="size-4 shrink-0 accent-emerald-500"
    />
  );
}
